import React, { useState, FunctionComponent, ReactNode, useEffect, useContext, useRef } from 'react'
import { AccordionContext } from './AccordionContext'
import { AccordionGroupContext } from './AccordionGroupContext'

import { ACCORDION_DISPLAY_NAME } from './constants'
import styles from './style.module.scss'
import clsx from 'clsx'
import { useThemeClass } from '../Theme'
import { useIsMobileBrowser } from '../useDevice'
import { AccessoryPositions } from '../Cell'

export interface AccordionProps {
  children: ReactNode
  defaultExpanded?: boolean
  disabled?: boolean
  id?: string
  onChange?: (isExpanded: boolean) => void
  testNameSpace?: string
  expanded?: boolean
  /** @deprecated Use `accessoryPosition` instead. */
  verticalAlignLabel?: boolean
  accessoryPosition?: AccessoryPositions
  animated?: boolean
  caret?: ReactNode
}

const defaultID = Math.random().toString(36)

export const Accordion: FunctionComponent<AccordionProps> = (props) => {
  const {
    children,
    defaultExpanded = false,
    disabled = false,
    id = defaultID,
    onChange,
    testNameSpace = '',
    verticalAlignLabel = false,
    accessoryPosition,
  } = props
  const isMobileBrowser = useIsMobileBrowser()
  const [isOpen, setIsOpen] = useState(props.expanded ?? defaultExpanded)
  const themeClass = useThemeClass()
  const componentClassNames = clsx(styles.accordion, styles[themeClass])

  // Get group context to support indirect hierarchy
  const groupContext = useContext(AccordionGroupContext)
  const isInGroup = Object.keys(groupContext).length > 0
  const accordionIdRef = useRef(id)

  // Determine if controlled by props or group
  const hasExpandedProp = 'expanded' in props
  const isGroupControlled = isInGroup && !hasExpandedProp

  // Determine expanded state
  const expandedState = isGroupControlled ? groupContext.expandedPanel === accordionIdRef.current : isOpen

  const handleAccordionToggle = (): void => {
    if (isGroupControlled) {
      // When in a group, delegate to group's onChange
      groupContext.onPanelChange?.(accordionIdRef.current)(!expandedState)
    } else if (!hasExpandedProp) {
      // Uncontrolled individual accordion
      setIsOpen(!isOpen)
    }
    // Always call the accordion's onChange if provided
    onChange?.(!expandedState)
  }

  useEffect(() => {
    if (hasExpandedProp && !isGroupControlled) {
      setIsOpen(props.expanded ?? false)
    }
  }, [props.expanded, hasExpandedProp, isGroupControlled])

  useEffect(() => {
    if (hasExpandedProp && 'defaultExpanded' in props && !isInGroup) {
      console.error(
        'Warning: Accordion elements must be either controlled or uncontrolled (specify either the expanded prop, or the defaultExpanded prop, but not both). Decide between using a controlled or uncontrolled Accordion element and remove one of these props. More info: https://reactjs.org/link/controlled-components'
      )
    }
  }, [props, isInGroup])

  return (
    <div className={componentClassNames} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}` })}>
      <AccordionContext.Provider
        value={{
          id: accordionIdRef.current,
          expanded: expandedState,
          onHeaderClick: handleAccordionToggle,
          disabled,
          verticalAlignLabel,
          accessoryPosition,
          testNameSpace,
          animated: props.animated ?? !isMobileBrowser,
          caret: props.caret,
        }}
      >
        {children}
      </AccordionContext.Provider>
    </div>
  )
}

Accordion.displayName = ACCORDION_DISPLAY_NAME
